import React, { ReactElement, useLayoutEffect, useState } from "react";
import Ext from "react-ext-state";
import { Button } from "finform";

import { TASK_FORM_TYPES } from "#app/store/reducers/task";
import { getTaskClients } from "#app/api/clients";
import { TaskContactDTO } from "#app/api/dto/contactDTO";

import ModalForm from "#app/components/forms/ModalForm/ModalForm";
import { useTaskForm } from "#app/pages/Relations/Tasks/hooks/useTaskForm";
import DeleteButton from "#app/components/Relations/shared/DeleteButton";
import useTaskActions from "#app/pages/Relations/Tasks/hooks/useTaskActions";
import NewTaskForm from "#app/pages/Relations/Tasks/form/TaskFormTypes/NewTaskForm";
import EventTaskForm from "#app/pages/Relations/Tasks/form/TaskFormTypes/EventTaskForm";

import { Row } from "#app/pages/styles";

export const CLIENT_TYPE = 2;
export const CONTACT_TYPE = 1;

interface TaskFormProps {
    children?: React.ReactNode;
    modalVisible: boolean;
    setModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
    form: unknown;
    taskType: { id: string; title: string };
    setTaskType: React.Dispatch<React.SetStateAction<{ id: string; title: string }>>;
    setForm: React.Dispatch<React.SetStateAction<unknown>>;
    callback: () => void;
    auth: { data: { id: number } };
}

const TaskForm = ({
    children,
    modalVisible,
    setModalVisible,
    form,
    taskType,
    setTaskType,
    setForm,
    callback,
    auth,
}: TaskFormProps): React.ReactNode => {
    if (!modalVisible) {
        return null;
    }
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [contacts, setContacts] = useState<TaskContactDTO[]>([]);

    useLayoutEffect(() => {
        if (modalVisible) getTaskClients(auth.data.id, setContacts);
    }, [modalVisible]);

    const callbackOnTaskUpdate = () => {
        callback();
        Ext.setState("crm-contact-task", 1);
    };

    const {
        id,
        setId,
        type,
        setType,
        typeId,
        setTypeId,
        comment,
        setComment,
        contactId,
        setContactId,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        repeatId,
        setRepeatId,
        closeForm,
        getError,
        changeFormErrors,
    } = useTaskForm(form, setModalVisible, setForm);

    const { handleDelete, handleUpdate, handleCreate } = useTaskActions(
        id,
        comment,
        startDate,
        endDate,
        typeId,
        contacts,
        contactId,
        repeatId,
        closeForm,
        callbackOnTaskUpdate,
        changeFormErrors,
        setId,
        loading,
        setLoading
    );

    const renderButtons = () => {
        return (
            <Row>
                {id && <DeleteButton handleDelete={handleDelete} />}
                <Row style={{ display: "block", textAlign: "center" }}>
                    <Button type="cancel" withMargin narrow onClick={closeForm}>
                        Отмена
                    </Button>
                    <Button
                        withMargin
                        narrow
                        loading={loading}
                        id="task-form__save-button"
                        onClick={id ? handleUpdate : handleCreate}
                    >
                        Сохранить
                    </Button>
                </Row>
            </Row>
        );
    };

    const getTaskName = () => {
        if (taskType.id === "event") {
            return taskType.title;
        } else return id ? "Редактирование" : "Новое событие";
    };

    // @TODO слишком много пропсов у NewTaskForm
    return (
        <ModalForm
            header={(() => getTaskName())()}
            modalVisible={modalVisible}
            closeForm={closeForm}
            width="760px"
            closeId="modal__close-task-form"
        >
            {taskType.id === TASK_FORM_TYPES.EVENT.id ? (
                <EventTaskForm
                    form={form}
                    id={id}
                    typeId={typeId}
                    startDate={startDate}
                    endDate={endDate}
                    setTaskType={setTaskType}
                    onUpdate={callbackOnTaskUpdate}
                />
            ) : (
                <NewTaskForm
                    type={type}
                    setType={setType}
                    typeId={typeId}
                    setTypeId={setTypeId}
                    comment={comment}
                    setComment={setComment}
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    contactId={contactId}
                    setContactId={setContactId}
                    search={search}
                    setSearch={setSearch}
                    repeatId={repeatId}
                    setRepeatId={setRepeatId}
                    renderButtons={renderButtons}
                    children={children}
                    contacts={contacts}
                    id={id}
                    getError={getError}
                />
            )}
        </ModalForm>
    );
};

export default TaskForm;
